import { useEffect, useMemo, useState } from 'react';
import LecturerSidebar from '../components/LecturerSidebar.js';
import AttendanceModal from '../components/AttendanceModal.js';
import '../styles/LecturerAttendance.css';

import {
  Eye,
  X,
  Search,
  Filter,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Users,
  CalendarDays,
} from 'lucide-react';

export default function LecturerAttendance() {
  // week | month | year
  const [period, setPeriod] = useState('year');

  // dane
  const [attendance, setAttendance] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);

  // loading
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [loadingRecentSessions, setLoadingRecentSessions] = useState(true);

  // UI / filtry
  const [groupFilter, setGroupFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [onlyUnchecked, setOnlyUnchecked] = useState(false);


  // panel szczegółów (wybrana sesja)
  const [selectedSession, setSelectedSession] = useState(null);

  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const openModal = (sessionId) => {
    setSelectedSessionId(sessionId);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedSessionId(null);
  };

  const getAttendanceClass = (rate) => {
    if (rate === null || rate === undefined) return 'att-badge-neutral';
    if (rate >= 90) return 'att-badge-green';
    if (rate >= 75) return 'att-badge-yellow';
    return 'att-badge-red';
  };

  const periodLabel = useMemo(() => {
    if (period === 'week') return 'Tygodniowa';
    if (period === 'month') return 'Miesięczna';
    return 'Roczna';
  }, [period]);

  // pobieranie danych
  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        setLoadingAttendance(true);
        const token = localStorage.getItem('token');

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/lecturer/attendance/summary?period=${period}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.ok) {
          const data = await res.json();
          setAttendance(Array.isArray(data) ? data : []);
        } else {
          console.error('Error fetching summary:', res.statusText);
          setAttendance([]);
        }
      } catch (err) {
        console.error('Error fetching summary:', err);
        setAttendance([]);
      } finally {
        setLoadingAttendance(false);
      }
    };

    const fetchRecentSessions = async () => {
      try {
        setLoadingRecentSessions(true);
        const token = localStorage.getItem('token');

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/lecturer/attendance/lastSessions`,
          { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.ok) {
          const data = await response.json();
          setRecentSessions(Array.isArray(data) ? data : []);
        } else {
          console.error('Error fetching recent sessions:', response.statusText);
          setRecentSessions([]);
        }
      } catch (error) {
        console.error('Error fetching recent sessions:', error);
        setRecentSessions([]);
      } finally {
        setLoadingRecentSessions(false);
      }
    };

    fetchAttendance();
    fetchRecentSessions();
  }, [period]);

  // opcje filtrów (grupy / moduły) wyciągane z danych
  const groupOptions = useMemo(() => {
    const set = new Set();
    attendance.forEach((s) => s?.group_code && set.add(s.group_code));
    return Array.from(set).sort();
  }, [attendance]);

  const moduleOptions = useMemo(() => {
    const set = new Set();
    attendance.forEach((s) => s?.module_name && set.add(s.module_name));
    return Array.from(set).sort();
  }, [attendance]);

  // filtrowanie listy zajęć
  const filteredAttendance = useMemo(() => {
    const q = query.trim().toLowerCase();

    return attendance.filter((s) => {
      if (!s) return false;

      if (groupFilter !== 'all' && s.group_code !== groupFilter) return false;
      if (moduleFilter !== 'all' && s.module_name !== moduleFilter) return false;

      if (onlyUnchecked && s.attendance_percentage !== null && s.attendance_percentage !== undefined) {
        return false;
      }

      if (!q) return true;

      const hay = [
        s.module_name,
        s.session_title,
        s.group_code,
        s.session_date,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [attendance, groupFilter, moduleFilter, onlyUnchecked, query]);

  // statystyki
  const stats = useMemo(() => {
    const total = filteredAttendance.length;

    const checked = filteredAttendance.filter(
      (s) => s.attendance_percentage !== null && s.attendance_percentage !== undefined
    ).length;

    const unchecked = total - checked;

    const avg = (() => {
      const vals = filteredAttendance
        .map((s) => s.attendance_percentage)
        .filter((v) => v !== null && v !== undefined);

      if (!vals.length) return null;

      const sum = vals.reduce((a, b) => a + Number(b || 0), 0);
      return Math.round((sum / vals.length) * 10) / 10;
    })();

    return { total, checked, unchecked, avg };
  }, [filteredAttendance]);

  // klik w wiersz -> panel szczegółów
  const handleSelectSession = (session) => {
    setSelectedSession(session);
  };

  const clearFilters = () => {
    setPeriod('year');
    setGroupFilter('all');
    setModuleFilter('all');
    setQuery('');
    setOnlyUnchecked(false);
    setSelectedSession(null);
  };

  return (
    <div className="lecture-dashboard-panel">
      <LecturerSidebar />

      <div className="main-content">
        <div className="la-page">
          {/* Top header */}
          <header className="la-topbar">
            <div className="la-topbar-left">
              <h1 className="la-h1">Obecności</h1>
              <p className="la-subtitle">Zarządzaj obecnością kursantów i monitoruj frekwencję</p>
            </div>

            <div className="la-topbar-actions">
              <button className="la-btn la-btn-ghost" type="button" onClick={clearFilters}>
                <X size={16} />
                Wyczyść
              </button>
            </div>
          </header>

          {/* Filters + stats */}
          <section className="la-card">
            <div className="la-card-head">
              <div>
                <h2 className="la-card-title">Frekwencja — {periodLabel}</h2>
                <p className="la-card-hint">Filtruj, wyszukuj i sprawdzaj obecność szybciej.</p>
              </div>

              <div className="la-card-icon">
                <Filter size={16} />
              </div>
            </div>

            <div className="la-filters">
              <div className="la-field">
                <label className="la-label">Widok</label>
                <select
                  className="la-select"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  <option value="year">Widok Roczny</option>
                  <option value="month">Widok Miesięczny</option>
                  <option value="week">Widok Tygodniowy</option>
                </select>
              </div>

              <div className="la-field">
                <label className="la-label">Grupa</label>
                <select
                  className="la-select"
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                >
                  <option value="all">Wszystkie</option>
                  {groupOptions.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              <div className="la-field">
                <label className="la-label">Kurs / Moduł</label>
                <select
                  className="la-select"
                  value={moduleFilter}
                  onChange={(e) => setModuleFilter(e.target.value)}
                >
                  <option value="all">Wszystkie</option>
                  {moduleOptions.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div className="la-field la-field-toggle">
                <label className="la-label"> </label>
                <label className="la-toggle">
                  <input
                    type="checkbox"
                    checked={onlyUnchecked}
                    onChange={(e) => setOnlyUnchecked(e.target.checked)}
                  />
                  <span>Tylko „Nie sprawdzono”</span>
                </label>
              </div>
            </div>

            <div className="la-stats">
              <div className="la-stat">
                <div className="la-stat-ic">
                  <CalendarDays size={16} />
                </div>
                <div>
                  <div className="la-stat-label">Zajęcia</div>
                  <div className="la-stat-value">{stats.total}</div>
                </div>
              </div>

              <div className="la-stat">
                <div className="la-stat-ic la-stat-ic-green">
                  <CheckCircle2 size={16} />
                </div>
                <div>
                  <div className="la-stat-label">Sprawdzone</div>
                  <div className="la-stat-value">{stats.checked}</div>
                </div>
              </div>

              <div className="la-stat">
                <div className="la-stat-ic la-stat-ic-yellow">
                  <AlertCircle size={16} />
                </div>
                <div>
                  <div className="la-stat-label">Nie sprawdzono</div>
                  <div className="la-stat-value">{stats.unchecked}</div>
                </div>
              </div>

              <div className="la-stat">
                <div className="la-stat-ic la-stat-ic-blue">
                  <BarChart3 size={16} />
                </div>
                <div>
                  <div className="la-stat-label">Śr. frekwencja</div>
                  <div className="la-stat-value">
                    {stats.avg === null ? '—' : `${stats.avg}%`}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* List + details */}
          <section className="la-grid">
            <div className="la-card la-card-table">
              <div className="la-card-head la-card-head-tight">
                <h3 className="la-card-title-sm">Lista zajęć</h3>
                <p className="la-card-note">
                  Kliknij wiersz, aby zobaczyć szczegóły po prawej. „Szczegóły” otworzy listę obecności.
                </p>
              </div>

              <div className="la-table-search">
                <div className="la-search la-search-enhanced">
                  <Search size={18} />
                  <input
                    className="la-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Szukaj po temacie, dacie, grupie lub kursie…"
                  />
                  {query && (
                    <button
                      type="button"
                      className="la-search-clear"
                      onClick={() => setQuery('')}
                      aria-label="Wyczyść wyszukiwanie"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="la-table-wrap">
                <table className="la-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Kurs / Temat</th>
                      <th>Grupa</th>
                      <th>Osób</th>
                      <th>Frekwencja</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loadingAttendance ? (
                      <tr>
                        <td colSpan="6" className="la-td-muted">Ładowanie danych…</td>
                      </tr>
                    ) : filteredAttendance.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="la-td-muted">Brak zajęć dla aktualnych filtrów.</td>
                      </tr>
                    ) : (
                      filteredAttendance.map((s) => {
                        const isActive = selectedSession?.session_id === s.session_id;
                        const percent = s.attendance_percentage;

                        return (
                          <tr
                            key={s.session_id}
                            className={isActive ? 'is-active' : ''}
                            onClick={() => handleSelectSession(s)}
                          >
                            <td className="la-date">{s.session_date}</td>
                            <td className="la-topic">
                              <div className="la-topic-title">{s.module_name} —</div>
                              <div className="la-topic-sub">{s.session_title}</div>
                            </td>
                            <td>
                              <span className="la-pill">{s.group_code}</span>
                            </td>
                            <td>
                              <span className="la-inline">
                                <Users size={14} />
                                {s.number_of_students}
                              </span>
                            </td>
                            <td>
                              {percent === null || percent === undefined ? (
                                <span className={`la-badge ${getAttendanceClass(null)}`}>Nie sprawdzono</span>
                              ) : (
                                <div className="la-progress">
                                  <div className="la-progress-bar">
                                    <div
                                      className={`la-progress-fill ${getAttendanceClass(percent)}`}
                                      style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
                                    />
                                  </div>
                                  <span className={`la-badge ${getAttendanceClass(percent)}`}>
                                    {percent}%
                                  </span>
                                </div>
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="la-btn la-btn-row"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openModal(s.session_id);
                                }}
                              >
                                <Eye size={16} />
                                Szczegóły
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <aside className="la-card la-card-details">
              {!selectedSession ? (
                <div className="la-details-empty">
                  <div className="la-details-title">Szczegóły zajęć</div>
                  <div className="la-details-sub">Wybierz zajęcia z tabeli</div>
                  <p className="la-details-text">
                    Kliknij wiersz w tabeli, aby zobaczyć szczegóły zajęć i szybko otworzyć listę obecności.
                  </p>
                </div>
              ) : (
                <div className="la-details">
                  <div className="la-details-top">
                    <div>
                      <div className="la-details-date">{selectedSession.session_date}</div>
                      <div className="la-details-module">{selectedSession.module_name}</div>
                      <div className="la-details-title2">{selectedSession.session_title}</div>
                    </div>
                    <span className="la-pill">{selectedSession.group_code}</span>
                  </div>

                  <div className="la-details-kpis">
                    <div className="la-kpi">
                      <div className="la-kpi-label">Liczba kursantów</div>
                      <div className="la-kpi-value">{selectedSession.number_of_students}</div>
                    </div>
                    <div className="la-kpi">
                      <div className="la-kpi-label">Status</div>
                      <div className="la-kpi-value">
                        {(selectedSession.attendance_percentage === null ||
                          selectedSession.attendance_percentage === undefined)
                          ? 'Nie sprawdzono'
                          : `${selectedSession.attendance_percentage}%`}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="la-btn la-btn-primary la-btn-full"
                    onClick={() => openModal(selectedSession.session_id)}
                  >
                    <Eye size={16} />
                    Otwórz listę obecności
                  </button>
                </div>
              )}
            </aside>
          </section>

          {/* Recent sessions – poziomo */}
          <section className="la-card">
            <div className="la-card-head la-card-head-tight">
              <h3 className="la-card-title-sm">Ostatnie zajęcia</h3>
              <p className="la-card-note">Poziomy pasek — kliknij kartę, aby ustawić szczegóły.</p>
            </div>

            {loadingRecentSessions ? (
              <div className="la-td-muted">Ładowanie danych…</div>
            ) : recentSessions.length === 0 ? (
              <div className="la-td-muted">Brak danych ostatnich zajęć.</div>
            ) : (
              <div className="la-recent">
                {recentSessions.map((s) => (
                  <button
                    key={s.session_id}
                    type="button"
                    className="la-recent-card"
                    onClick={() => {
                      // ustawiamy tylko podgląd (na bazie recent), a modal otwierasz ręcznie
                      setSelectedSession((prev) => {
                        // jeżeli mamy sesję w summary – preferuj ją (pełniejsze dane)
                        const fromSummary = attendance.find((x) => x.session_id === s.session_id);
                        return fromSummary || {
                          session_id: s.session_id,
                          session_date: s.session_date,
                          module_name: s.module_name,
                          session_title: s.session_title,
                          group_code: s.group_code,
                          number_of_students: s.number_of_students ?? (s.present_count || 0) + (s.late_count || 0) + (s.absent_count || 0),
                          attendance_percentage: s.attendance_rate ?? null,
                        };
                      });
                    }}
                  >
                    <div className="la-recent-top">
                      <div className="la-recent-date">{s.session_date}</div>
                      <span className={`la-badge ${getAttendanceClass(s.attendance_rate ?? null)}`}>
                        {s.attendance_rate === null || s.attendance_rate === undefined ? '—' : `${s.attendance_rate}%`}
                      </span>
                    </div>

                    <div className="la-recent-title">{s.module_name} —</div>
                    <div className="la-recent-sub">{s.session_title}</div>

                    <div className="la-recent-meta">
                      <span>Obecni: {s.present_count || 0}</span>
                      <span>Spóźnieni: {s.late_count || 0}</span>
                      <span>Nieobecni: {s.absent_count || 0}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      <AttendanceModal
        sessionId={selectedSessionId}
        isOpen={modalOpen}
        onClose={closeModal}
      />
    </div>
  );
}
